import { useMemo, useState } from "react";
import { photoCategories, photographs } from "../data/mock";
import { X } from "lucide-react";

const Photography = () => {
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const filtered = useMemo(() => {
    if (active === "all") return photographs;
    return photographs.filter((p) => p.category === active);
  }, [active]);

  return (
    <section id="photography" className="py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="reveal">
            <div className="text-xs tracking-[0.25em] uppercase text-[#8B6F47] mb-4">(02) Photography</div>
            <h2 className="font-display text-[48px] md:text-[76px] leading-[0.98] tracking-tight">
              Selected <span className="italic-display">stills</span>.
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-[#1A1A1A]/70 reveal">
            A rotating archive of recent work — engagements, local businesses, family lifestyle,
            senior portraits, and live music. Click any image to enlarge.
          </p>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 md:gap-3 mb-10 reveal">
          {photoCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`px-5 py-2.5 rounded-full text-sm tracking-wide border transition-colors duration-300 ${
                active === c.id
                  ? "bg-[#1A1A1A] text-[#F5F1EA] border-[#1A1A1A]"
                  : "bg-transparent text-[#1A1A1A] border-[#1A1A1A]/25 hover:border-[#1A1A1A]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Masonry Grid */}
        <div className="columns-2 md:columns-3 gap-3 md:gap-6 [column-fill:_balance]">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => setLightbox(p)}
              className="group relative hover-zoom bg-[#EEE8DB] overflow-hidden mb-3 md:mb-6 block w-full break-inside-avoid"
            >
              <img
                src={p.src}
                alt={p.alt}
                className="w-full h-auto object-cover block"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute left-4 bottom-4 right-4 text-left opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                <div className="text-[#F5F1EA] font-display text-xl leading-tight">{p.title}</div>
                <div className="text-[#F5F1EA]/80 text-[11px] tracking-[0.2em] uppercase mt-1">
                  {p.location} · {p.year}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-[#F5F1EA] hover:text-[#8B6F47] transition-colors"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-w-[90vw] max-h-[78vh] object-contain"
            />
            <div className="mt-4 text-[#F5F1EA] flex items-center justify-between">
              <div>
                <div className="font-display text-2xl">{lightbox.title}</div>
                <div className="text-[11px] tracking-[0.2em] uppercase opacity-70 mt-1">
                  {lightbox.location} · {lightbox.year}
                </div>
              </div>
              <div className="text-[11px] tracking-[0.2em] uppercase opacity-70">
                {photoCategories.find((c) => c.id === lightbox.category)?.label}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Photography;
